'use client';

import {
  Shield,
  Lock,
  ShieldCheck,
  Key,
  Users,
  FileCode,
  Warning,
  ArrowRight,
  Buildings,
  IdentificationCard,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { AnimatedSection } from '@/components/marketing/AnimatedSection';
import { IconCardGrid } from '@/components/marketing/IconCardGrid';
import { PageHero } from '@/components/marketing/PageHero';

const practices = [
  {
    icon: Buildings,
    title: 'Workspace isolation',
    description:
      'Each contractor organization is a separate workspace. Users sign into their company, and workspace separation is enforced with database row-level policies that our isolation audit program reviews continuously.',
  },
  {
    icon: Lock,
    title: 'Encryption in transit and at rest',
    description:
      'Data moves over TLS. At-rest storage encryption is handled by our managed database layer, and application-level field encryption is on the roadmap. We do not store payment card data on Rooflyzer servers.',
  },
  {
    icon: ShieldCheck,
    title: 'Least-privilege roles',
    description:
      'Owner, sales, production, office, and accountant views. Billing, domain, and workspace settings stay with administrators.',
  },
  {
    icon: Key,
    title: 'Identity',
    description:
      'Email and password, Google sign-in, and phone sign-in. Sessions are designed for a multi-user office. Two-factor authentication is on the roadmap.',
  },
  {
    icon: Users,
    title: 'Edge protection',
    description:
      'A web application firewall and bot controls sit in front of public forms and application login.',
  },
  {
    icon: IdentificationCard,
    title: 'Auditability',
    description:
      'Sensitive actions on leads, money, and access can be reviewed by administrators. We do not publish log schemas or runbooks on this site.',
  },
  {
    icon: FileCode,
    title: 'Vulnerability process',
    description:
      'We patch dependencies and accept responsible disclosure. Critical patches are applied promptly. We do not publish vulnerability tickets here.',
  },
  {
    icon: Shield,
    title: 'Buyer packet, not a diagram',
    description:
      'Security questionnaires and DPA review happen with the team. This page states posture. It does not publish infrastructure names or implementation internals.',
  },
];

const securitySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Security \u2014 Rooflyzer',
  description:
    'Rooflyzer security posture for roofing contractors: workspace isolation, TLS, and roles.',
  url: 'https://rooflyzer.pro/security',
};

const securityArticleSchema = {
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: 'Rooflyzer Security Posture',
  about: ['TLS', 'workspace isolation', 'WAF'],
  description:
    'Buyer-facing overview of Rooflyzer security posture including encryption, workspace isolation, access control, and vulnerability handling.',
  url: 'https://rooflyzer.pro/security',
  author: { '@type': 'Organization', name: 'Rooflyzer' },
  publisher: { '@type': 'Organization', name: 'Rooflyzer' },
};

/** Present the platform's security practices and structured search data. */
export const SecurityPage = () => {
  return (
    <>
      <SEOHead
        title="Security"
        description="Workspace isolation, TLS, and role-based access. SOC 2 is on the roadmap. Request a security review for a buyer packet."
        canonicalUrl="/security"
        keywords="roofing CRM security, roofing data protection, roofing software security, workspace isolation"
        ogImage="/social-card.svg"
        schema={[securitySchema, securityArticleSchema]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Security', url: '/security' },
        ]}
      />

      <PageHero
        badgeIcon={Shield}
        badgeLabel="Security"
        title={
          <>
            Posture a buyer can put in a packet.{' '}
            <span className="text-gradient-gold">Not a diagram of our internals.</span>
          </>
        }
        lead={
          <>
            This page is for owners, IT, and insurers. It states how we isolate workspaces, encrypt
            data, and handle access. It does not publish implementation internals or vulnerability
            tickets.
          </>
        }
      />

      <IconCardGrid items={practices} />

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="rounded-3xl bg-white/[0.03] border border-white/[0.08] p-8 md:p-12 flex flex-col lg:flex-row items-center gap-10 overflow-hidden">
              <div className="flex-1 space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider">
                  <Warning className="w-4 h-4 text-amber-400" />
                  <span>Honest status</span>
                </div>
                <h2 className="section-h2 font-bold text-white">SOC 2 is on the roadmap</h2>
                <p className="body-lg text-white/60">
                  We will not put a certification badge on this site until a report exists. Data
                  export is covered by the public Data Freedom Pledge. Send a questionnaire to
                  support@rooflyzer.pro and we will answer the packet.
                </p>
                <ul className="space-y-3 text-white/70">
                  {[
                    'Data belongs to you. Export or delete your records at any time.',
                    'Roles limit who can see billing, jobs, and workspace settings.',
                    'TLS in transit. Isolation audited.',
                    'WAF and bot controls in front of public forms and login.',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <ShieldCheck
                        className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5"
                        weight="bold"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <Button
                    size="lg"
                    className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                    asChild
                  >
                    <Link to="/contact">
                      Request security review
                      <ArrowRight className="ml-2 size-5" />
                    </Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-lg border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60 px-8"
                    asChild
                  >
                    <Link to="/data-freedom">Data Freedom Pledge</Link>
                  </Button>
                </div>
              </div>
              <div className="lg:w-2/5">
                <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8 text-left">
                  <p className="text-xs uppercase tracking-wider text-white/40 mb-3">
                    Enterprise path
                  </p>
                  <p className="text-white font-semibold mb-2">Need invoice, DPA, or SSO?</p>
                  <p className="text-white/55 text-sm leading-relaxed mb-6">
                    Business at $99 per active user per month has no user cap and includes
                    white-glove onboarding. Procurement questions go through the Enterprise page,
                    not a public internals dump.
                  </p>
                  <Button
                    asChild
                    variant="outline"
                    className="rounded-full border-white/15 text-white hover:bg-white/5"
                  >
                    <Link to="/enterprise">
                      Enterprise overview
                      <ArrowRight className="ml-2 size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};
